import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import { IImageBlockDisplayOptions } from 'src/interfaces/PageBlock';

interface Props {
  value: IImageBlockDisplayOptions;
  onChange: (patch: Partial<IImageBlockDisplayOptions>) => void;
  showTitle?: boolean;
}

export default function ImageDisplayFields({ value, onChange, showTitle = false }: Props) {
  const { t } = useTranslation();

  const handleChange =
    (field: keyof IImageBlockDisplayOptions) =>
    (event: { target: { value: string } }) => {
      onChange({ [field]: event.target.value });
    };

  return (
    <Grid container spacing={2}>
      {showTitle ? (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t('editor.imageEdit.imageTitle')}
            value={value.title ?? ''}
            onChange={handleChange('title')}
            variant="outlined"
            size="small"
          />
        </Grid>
      ) : null}

      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label={t('editor.imageEdit.width')}
          value={value.width ?? ''}
          onChange={handleChange('width')}
          placeholder="e.g., 300px or 50%"
          variant="outlined"
          size="small"
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <TextField
          fullWidth
          label={t('editor.imageEdit.height')}
          value={value.height ?? ''}
          onChange={handleChange('height')}
          placeholder="e.g., 200px or auto"
          variant="outlined"
          size="small"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('editor.imageEdit.alignment')}</InputLabel>
          <Select
            value={value.alignment ?? ''}
            onChange={handleChange('alignment')}
            label={t('editor.imageEdit.alignment')}
          >
            <MenuItem value="">{t('editor.imageEdit.alignment.none')}</MenuItem>
            <MenuItem value="img-left">{t('editor.imageEdit.alignment.left')}</MenuItem>
            <MenuItem value="img-center">{t('editor.imageEdit.alignment.center')}</MenuItem>
            <MenuItem value="img-right">{t('editor.imageEdit.alignment.right')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('editor.imageEdit.size')}</InputLabel>
          <Select
            value={value.size ?? ''}
            onChange={handleChange('size')}
            label={t('editor.imageEdit.size')}
          >
            <MenuItem value="">{t('editor.imageEdit.size.original')}</MenuItem>
            <MenuItem value="img-small">{t('editor.imageEdit.size.small')}</MenuItem>
            <MenuItem value="img-medium">{t('editor.imageEdit.size.medium')}</MenuItem>
            <MenuItem value="img-large">{t('editor.imageEdit.size.large')}</MenuItem>
            <MenuItem value="img-full">{t('editor.imageEdit.size.full')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{t('editor.imageEdit.style')}</InputLabel>
          <Select
            value={value.style ?? ''}
            onChange={handleChange('style')}
            label={t('editor.imageEdit.style')}
          >
            <MenuItem value="">{t('editor.imageEdit.style.none')}</MenuItem>
            <MenuItem value="img-rounded">{t('editor.imageEdit.style.rounded')}</MenuItem>
            <MenuItem value="img-circle">{t('editor.imageEdit.style.circle')}</MenuItem>
            <MenuItem value="img-shadow">{t('editor.imageEdit.style.shadow')}</MenuItem>
            <MenuItem value="img-border">{t('editor.imageEdit.style.border')}</MenuItem>
            <MenuItem value="img-thumbnail">{t('editor.imageEdit.style.thumbnail')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}
